import React from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Image,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BrandAvatar } from "../../components/BrandAvatar";
import { Link as RouterLink } from "react-router-dom";
import {
  FiBarChart2,
  FiMapPin,
  FiCalendar,
  FiSearch,
  FiUserPlus,
} from "react-icons/fi";
import { format } from "date-fns";
import NoticeBoardCard from "./NoticeBoardCard";
import GrowCard from "./GrowCard";
import ExpertiseCard from "./ExpertiseCard";
import LatestPodcastsCard from "./LatestPodcastsCard";
import { SurveyListPanel } from "../../components/surveys/SurveyListPanel";
import {
  sendConnectionRequest,
  checkConnectionStatus,
} from "../../services/connectionsService";
import {
  readExpertiseFromRow,
  readLocalExpertise,
} from "../../services/expertiseService";
import { isAcademicProfile } from "../../constants/expertise";

// Three keeps the panel short enough that the cards below it stay visible.
// The backend ranks by shared themes, mutual connections, org and country, so
// the top few are the ones worth showing.
const SUGGESTION_COUNT = 3;

// A single "suggested connection" row. Clicking the name/avatar opens the
// person's profile; the Connect button actually sends a follow/connection
// request (POST /follow/request) and reflects idle → sending → requested.
function SuggestedConnectionRow({ p, t, cardMuted, describeAffiliation, onView }) {
  const targetId = p.user_id || p.id;
  const name =
    `${(p.first_name || "").trim()} ${(p.last_name || "").trim()}`.trim() ||
    t("dashboard.member");
  const descriptor = describeAffiliation(p);
  const [status, setStatus] = React.useState("idle"); // idle | sending | requested | error

  React.useEffect(() => {
    let cancelled = false;
    if (!targetId) return undefined;
    checkConnectionStatus(targetId)
      .then((s) => {
        if (cancelled || !s) return;
        const norm = String(s).toLowerCase();
        if (["pending", "accepted", "connected", "following"].includes(norm)) {
          setStatus("requested");
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [targetId]);

  const handleConnect = async (e) => {
    e.stopPropagation();
    if (status === "sending" || status === "requested" || !targetId) return;
    setStatus("sending");
    try {
      await sendConnectionRequest(targetId);
      setStatus("requested");
    } catch (err) {
      const msg = String(err?.message || "").toLowerCase();
      // Backend already has a pending/existing edge → treat as requested
      if (
        msg.includes("pending") ||
        msg.includes("exist") ||
        msg.includes("already") ||
        msg.includes("409")
      ) {
        setStatus("requested");
      } else {
        setStatus("error");
      }
    }
  };

  const label =
    status === "requested"
      ? t("dashboard.requested", "Requested")
      : status === "error"
      ? t("dashboard.retry", "Retry")
      : t("dashboard.connect", "Connect");

  return (
    <HStack justify="space-between" align="start" spacing={2} w="100%">
      <HStack
        flex="1"
        minW={0}
        align="start"
        spacing={3}
        cursor="pointer"
        onClick={onView}
        _hover={{ opacity: 0.9 }}
      >
        <BrandAvatar size="sm" name={name} src={p.avatar_url || undefined} />
        <VStack align="start" spacing={0} minW={0}>
          <Text fontWeight="bold" fontSize="sm">
            {name}
          </Text>
          {/* describeAffiliation joins every industry category a person picked,
              which wrapped one row over three or four lines. Clamp it and keep
              the full text on the native hover tooltip. */}
          {descriptor && (
            <Text fontSize="xs" color={cardMuted} noOfLines={1} title={descriptor}>
              {descriptor}
            </Text>
          )}
        </VStack>
      </HStack>
      <Button
        size="xs"
        variant="ghost"
        colorScheme="gwbi"
        flexShrink={0}
        isLoading={status === "sending"}
        isDisabled={status === "requested"}
        onClick={handleConnect}
      >
        {label}
      </Button>
    </HStack>
  );
}

export const RightRail = ({
  cardBg,
  cardText,
  cardTitle,
  cardMuted,
  latestProfiles,
  connectedUserIds,
  connectedEmails,
  describeAffiliation,
  viewExternalProfile,
  user,
  profile,
  t,
  conferences,
  gwbiEvents,
  onAddNotice,
  notices,
  onStartSurvey,
  onCreateSurvey,
  onManageSurveys,
}) => (
  <VStack spacing={4} align="stretch">
    <NoticeBoardCard
      cardBg={cardBg}
      cardText={cardText}
      cardTitle={cardTitle}
      cardMuted={cardMuted}
      t={t}
      notices={notices}
      onAddNotice={onAddNotice}
    />

    <Box
      bg={cardBg}
      color={cardTitle}
      borderRadius="ui-lg"
      overflow="hidden"
      boxShadow="ui-sm"
      borderWidth="1px"
      borderColor="border.default"
      p={4}
      position="relative"
      >
      <HStack mb={0}>
        <Icon as={FiUserPlus} />
        <Text textStyle="cardTitle" color={cardTitle}>
          {t("right_rail.collaboration")}
        </Text>
      </HStack>
      <Text mt={1} fontSize="xs" color={cardMuted} pb={2}>
        {t("right_rail.suggested_connections")}
      </Text>
      <VStack align="stretch" spacing={3}>
        {latestProfiles.length === 0 ? (
          <Text fontSize="xs" color={cardMuted}>
            {t("right_rail.no_suggestions")}
          </Text>
        ) : (
          // Filter BEFORE limiting: slicing first meant already-connected
          // people consumed slots and rendered as nothing, so the card often
          // showed fewer rows than intended — sometimes none at all.
          latestProfiles
            .filter((p) => {
              const norm = (v) => (v ? String(v).trim().toLowerCase() : "");
              const pEmail = norm(
                p.email ||
                  p.user?.email ||
                  p.profile?.email ||
                  p.contact_email ||
                  p.primary_email
              );
              if (connectedUserIds.includes(p.user_id || p.id)) return false;
              if (pEmail && connectedEmails.includes(pEmail)) return false;
              const currentUserId = user?.id || profile?.id;
              if (
                currentUserId &&
                (p.user_id === currentUserId || p.id === currentUserId)
              )
                return false;
              return true;
            })
            .slice(0, SUGGESTION_COUNT)
            .map((p, idx) => {
            return (
              <SuggestedConnectionRow
                key={p.user_id || p.id || idx}
                p={p}
                t={t}
                cardMuted={cardMuted}
                describeAffiliation={describeAffiliation}
                onView={() => viewExternalProfile(p.user_id || p.id)}
              />
            );
          })
        )}
      </VStack>

      {/* Suggestions answer "who is like me?". This answers "who can do the
          thing my project needs?", which is the other half of finding people.
          Shown to academics and students only, like the rest of Expertise. */}
      {isAcademicProfile(profile) && (
      <Button
        as={RouterLink}
        to="/expertise/search"
        size="xs"
        variant="ghost"
        colorScheme="gwbi"
        leftIcon={<FiSearch />}
        mt={3}
        w="100%"
      >
        {t("right_rail.search_by_expertise", "Search by expertise")}
      </Button>
      )}
    </Box>

    {/* The multiplier: members bring the next members. Kept next to the
        people card because that is the thought it follows on from. */}
    <GrowCard
      cardBg={cardBg}
      cardText={cardText}
      cardTitle={cardTitle}
      cardMuted={cardMuted}
      t={t}
    />

    {/* The Expertise tool. Reads the saved answer from the profile when the
        API carries it and from the browser mirror until it does, so the card
        flips from advertisement to summary the moment the member finishes. */}
    {isAcademicProfile(profile) && (
    <ExpertiseCard
      cardBg={cardBg}
      cardText={cardText}
      cardTitle={cardTitle}
      cardMuted={cardMuted}
      t={t}
      skillIds={(() => {
        const fromProfile = readExpertiseFromRow(profile || {});
        if (fromProfile.length) return fromProfile;
        return readLocalExpertise(user?.id || profile?.id);
      })()}
    />
    )}

    <LinkBox
      as="article"
      bg={cardBg}
      color={cardText}
      borderRadius="ui-lg"
      overflow="hidden"
      boxShadow="ui-sm"
      borderWidth="1px"
      borderColor="border.default"
      transition="all .2s"
      _hover={{ boxShadow: "ui-md", transform: "translateY(-3px)" }}
    >
      <Image
        src="/vw_card.webp"
        alt=""
        w="100%"
        h="150px"
        objectFit="cover"
        objectPosition="center 38%"
        borderBottomWidth="1px"
        borderColor="border.default"
      />
      <Box p={4}>
        <HStack spacing={1.5} mb={1} color="gwbi.600">
          <Icon as={FiBarChart2} boxSize={3.5} />
          <Text
            fontSize="2xs"
            fontWeight="bold"
            letterSpacing="0.16em"
            textTransform="uppercase"
          >
            {t("right_rail.dataset_tools", "Visualising Wine")}
          </Text>
        </HStack>
        <Heading fontSize="md" lineHeight="1.2" color={cardTitle} mb={1}>
          <LinkOverlay as={RouterLink} to="/visualising-wine">
            {t("right_rail.vinoverse_title", "The interactive Wine Atlas")}
          </LinkOverlay>
        </Heading>
        <Text fontSize="xs" color={cardMuted} mb={2}>
          {t("right_rail.vinoverse_subtitle", "Maps, climate stories & country scorecards")}
        </Text>
        <Text fontSize="sm" fontWeight="semibold" color="gwbi.600">
          {t("right_rail.open", "Open")} →
        </Text>
      </Box>
    </LinkBox>

    <SurveyListPanel
      cardBg={cardBg}
      cardTitle={cardTitle}
      cardMuted={cardMuted}
      onStartSurvey={onStartSurvey}
      onCreateSurvey={onCreateSurvey}
      onManageSurveys={onManageSurveys}
    />

    <Box
      bg={cardBg}
      color={cardTitle}
      borderRadius="ui-lg"
      overflow="hidden"
      boxShadow="ui-sm"
      borderWidth="1px"
      borderColor="border.default"
      p={4}
    >
      <HStack mb={2}>
        <Icon as={FiCalendar} />
        <Text textStyle="cardTitle" color={cardTitle}>
          {t("right_rail.upcoming_events")}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {(() => {
          const now = new Date();
          // Normalise both sources into one shape; tag internal vs external.
          const norm = (e, internal) => {
            const raw =
              e?.starts_at || e?.date_conference || e?.start_date || e?.date;
            return {
              key: `${internal ? "int" : "ext"}-${e?.id ?? Math.random()}`,
              id: e?.id,
              internal,
              title: e?.title || e?.name || t("right_rail.untitled_event"),
              date: raw ? new Date(raw) : null,
              location: e?.location || e?.city || "",
              url: e?.source_url || e?.website_url || e?.link || e?.url || null,
            };
          };
          const all = [
            ...(Array.isArray(conferences) ? conferences.map((e) => norm(e, false)) : []),
            ...(Array.isArray(gwbiEvents) ? gwbiEvents.map((e) => norm(e, true)) : []),
          ];
          const upcoming = all
            .filter((e) => e.date && !Number.isNaN(e.date.getTime()) && e.date >= now)
            .sort((a, b) => a.date - b.date)
            .slice(0, 5);
          if (upcoming.length === 0) {
            return (
              <Text fontSize="xs" color={cardMuted}>
                {t("right_rail.no_upcoming_events")}
              </Text>
            );
          }

          return upcoming.map((e) => {
            const dateLabel = format(e.date, "EEE dd MMM yyyy");
            // Internal events have a real detail page; external open their source.
            const linkProps = e.internal
              ? (e.id
                  ? { as: RouterLink, to: `/events/${e.id}`, cursor: "pointer" }
                  : { cursor: "default" })
              : (e.url
                  ? { as: "a", href: e.url, target: "_blank", rel: "noopener noreferrer", cursor: "pointer" }
                  : { cursor: "default" });
            const clickable = e.internal ? !!e.id : !!e.url;
            return (
              <Box
                key={e.key}
                {...linkProps}
                _hover={clickable ? { bg: "surface.subtle", textDecoration: "none" } : {}}
                borderRadius="md"
                p={2}
                mx={-2}
                transition="background 0.15s ease"
              >
                <HStack justify="space-between" align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="sm" noOfLines={2}>
                    {e.title}
                  </Text>
                  <Badge
                    flexShrink={0}
                    borderRadius="full"
                    textTransform="none"
                    fontSize="0.6rem"
                    colorScheme={e.internal ? "gwbi" : "gray"}
                    variant={e.internal ? "solid" : "subtle"}
                  >
                    {e.internal
                      ? t("right_rail.internal", "Internal")
                      : t("right_rail.external", "External")}
                  </Badge>
                </HStack>
                {e.location && (
                  <HStack spacing={1} mt={0.5} color={cardMuted}>
                    <Icon as={FiMapPin} boxSize={3} flexShrink={0} />
                    <Text fontSize="xs" noOfLines={1}>
                      {e.location}
                    </Text>
                  </HStack>
                )}
                <Text fontSize="xs" color={cardMuted} mt={0.5}>
                  {dateLabel}
                </Text>
              </Box>
            );
          });
        })()}
      </VStack>
    </Box>

    <LatestPodcastsCard
      cardBg={cardBg}
      cardTitle={cardTitle}
      cardMuted={cardMuted}
    />
  </VStack>
);
